import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLogin } from '@/hooks/api';
import { useAuth } from '@/hooks/useAuth';
import { LoginFormData } from '@/types';
import { ValidationUtils } from '@/utils/validation';
import { createApiError } from '@/utils/errorHandler';
import { TokenManager } from '@/utils/tokenManager';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const loginMutation = useLogin();
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');

  // Redirecionar se já estiver autenticado
  useEffect(() => {
    if (!loading && user) {
      navigate('/Home', { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (error || loginMutation.error) {
      setError('');
    }
  }, [formData.email, formData.password, error, loginMutation.error]);

  const handleInputChange = (field: keyof LoginFormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');

    try {
      ValidationUtils.validateLoginForm(formData.email, formData.password);

      const result = await loginMutation.mutateAsync({
        email: formData.email.trim(),
        password: formData.password
      });

      if (!result.success) {
        setError(result.error || 'Falha no login. Tente novamente.');
        return;
      }

      // Verificar se o token está realmente armazenado após login
      const token = TokenManager.getToken();

      if (!token) {
        setError('Erro ao processar autenticação. Tente novamente.');
        return;
      }

      navigate('/Home', { replace: true });
    } catch (error) {
      if (import.meta.env.DEV) console.error('[Login] Erro no processo de login:', error);
      const appError = createApiError(error, 'Login form submission');
      setError(appError.message);
    }
  };

  const displayError = error || loginMutation.error?.message;
  const isLoading = loginMutation.isPending;

  return (
    <div className='flex flex-col items-center justify-between h-screen bg-gradient-to-br from-[#1A211A] via-[#2a3a2a] to-[#1A211A] relative overflow-hidden'>
      <div className="flex flex-col items-center justify-center pt-[15vh] z-[2]">
        <div className="w-20 h-20 bg-gradient-to-br from-[#00A335] to-[#00d444] rounded-full flex items-center justify-center mb-6 shadow-[0_10px_30px_rgba(0,163,53,0.3)] animate-pulse">
          <span className="text-[2.5rem]">🌱</span>
        </div>
        <h1 className="text-[2.5rem] font-bold text-white mb-2 [text-shadow:0_2px_10px_rgba(0,0,0,0.3)]">EconSciente</h1>
        <p className="text-base text-[#EE9300] text-center px-8">Transformando hábitos, preservando o futuro</p>
      </div>

      <div>
        <form className='flex flex-col items-center gap-5 py-8 bg-[rgba(26,33,26,0.98)] backdrop-blur-[10px] rounded-tl-[35px] rounded-tr-[35px] shadow-[0_-10px_40px_rgba(0,0,0,0.3)] w-[100vw] z-[2]' onSubmit={handleLogin}>
          <div className="relative w-4/5 max-w-[400px]">
            <span className="absolute left-[15px] top-1/2 -translate-y-1/2 text-xl z-[1]">📧</span>
            <input
              data-testid="login-email"
              className="w-[100%] h-[50px] pl-[50px] pr-[15px] rounded-xl border-2 border-transparent bg-[rgba(50,52,65,0.8)] text-white text-base outline-none transition-all duration-300 placeholder:text-[rgba(255,255,255,0.5)] focus:border-[#00A335] focus:bg-[rgba(50,52,65,1)] focus:shadow-[0_0_0_4px_rgba(0,163,53,0.1)] focus:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
              type="email"
              placeholder="Digite seu e-mail"
              value={formData.email}
              onChange={handleInputChange('email')}
              disabled={isLoading}
              required
            />
          </div>

          <div className="relative w-4/5 max-w-[400px]">
            <span className="absolute left-[15px] top-1/2 -translate-y-1/2 text-xl z-[1]">🔒</span>
            <input
              data-testid="login-password"
              className="w-[100%] h-[50px] pl-[50px] pr-[15px] rounded-xl border-2 border-transparent bg-[rgba(50,52,65,0.8)] text-white text-base outline-none transition-all duration-300 placeholder:text-[rgba(255,255,255,0.5)] focus:border-[#00A335] focus:bg-[rgba(50,52,65,1)] focus:shadow-[0_0_0_4px_rgba(0,163,53,0.1)] focus:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
              type="password"
              placeholder="Digite sua senha"
              value={formData.password}
              onChange={handleInputChange('password')}
              disabled={isLoading}
              required
            />
          </div>

          <button
            data-testid="login-submit"
            className='w-[80%] max-w-[400px] h-[50px] rounded-xl border-none bg-gradient-to-br from-[#EE9300] to-[#ff9e00] text-white text-[1.1rem] font-semibold cursor-pointer transition-all duration-300 shadow-[0_4px_15px_rgba(238,147,0,0.3)] mt-2 hover:not-disabled:-translate-y-0.5 hover:not-disabled:shadow-[0_6px_20px_rgba(238,147,0,0.4)] active:not-disabled:translate-y-0 disabled:opacity-70 disabled:cursor-not-allowed'
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2.5">
                <span className="w-4 h-4 border-2 border-[rgba(255,255,255,0.3)] border-t-white rounded-full animate-spin"></span>
                Entrando...
              </span>
            ) : (
              'Entrar'
            )}
          </button>

          <Link className="no-underline text-[rgba(255,255,255,0.8)] text-[0.95rem] transition-colors duration-300 hover:text-white" to="/Cadastrar">
            <p className="m-0">Ainda não tem uma conta?
              <span className="text-[#EE9300] font-semibold"> Cadastre-se</span>
            </p>
          </Link>

          {displayError && (
            <div className="flex items-center gap-2.5 bg-[rgba(220,38,38,0.15)] border border-[rgba(220,38,38,0.3)] rounded-[10px] px-4 py-3 text-[#ff6b6b] text-[0.9rem] w-full max-w-[400px]">
              <span className="text-xl">⚠️</span>
              <span>{displayError}</span>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Login;